package ui.company.detail

import androidx.compose.runtime.*
import androidx.compose.ui.graphics.vector.*
import api.*
import common.frontend.api.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*
import ui.*
import ui.widget.*


interface CompanyScope {

    val vm: CompanyVM
    val vmGlobal: GlobalVM
    val args: CompanyArgs
    val nav: Navigation
    val login: Io.User
    val action: (CompanyVM.Action) -> Unit
    val display: CommonDisplayProvider

    companion object {
        @Composable
        fun rememberScope(): CompanyScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(CompanyArgs::class)
            val vm = nav.viewModel(CompanyVM::class, args.companyId.toString())
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = CompanyVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : CompanyScope {
                    override val vm: CompanyVM
                        get() = vm
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val args: CompanyArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val action: (CompanyVM.Action) -> Unit
                        get() = action
                    override val display: CommonDisplayProvider
                        get() = display
                }.also {
                    vm.init(it)
                }
            }
        }
    }


    class Wrapper(
        val canEdit: Boolean,
        val canAdd: Boolean,
        val canDelete: Boolean,
        override val data: Io.Company,
        override val key: String = data.id.toString()
    ) : EFieldList.Entry<Io.Company> {

        private var origin = makeCopy()

        val name: EFieldTextString = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 3 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val campaignType: EFieldRegularInt = EFieldRegularInt(
            property = data.campaign::type,
            onChange = {
                makeChange()
                when (CampaignType[it]) {
                    CampaignType.IMPRESSION -> {
                        campaignItemCount.set("12".toTextFieldValue())
                        campaignItemDuration.set("10".toTextFieldValue())
                    }

                    CampaignType.NONE -> Unit
                    CampaignType.SLOT -> {
                        campaignItemCount.set("10".toTextFieldValue())
                        campaignItemDuration.set("10".toTextFieldValue())
                    }
                }
            })

        val campaignItemCount: EFieldTextInt = EFieldTextInt(
            condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } },
            property = data.campaign::itemCount,
            onChange = { makeChange() })

        val campaignItemDuration: EFieldTextMsAsSec = EFieldTextMsAsSec(
            condition = { it.trim().toLongOrNull()?.takeIf { it > 0L } },
            property = data.campaign::itemDuration,
            onChange = { makeChange() })

        val enableCommercial: EFieldRegularBoolean = EFieldRegularBoolean(
            property = data.campaign::enableCommercial,
            onChange = { makeChange() })

        val enableCommercialAsNonCommercial: EFieldRegularBoolean = EFieldRegularBoolean(
            property = data.campaign::enableCommercialAsNonCommercial,
            onChange = { makeChange() })

        val campaignResourcesIgnoreAspectRatio: EFieldRegularBoolean = EFieldRegularBoolean(
            property = data.campaign.resources::ignoreAspectRatio,
            onChange = { makeChange() })
        val campaignResourcesIgnoreDuration: EFieldRegularBoolean = EFieldRegularBoolean(
            property = data.campaign.resources::ignoreDuration,
            onChange = { makeChange() })

//        val campaignResourcesIgnoreRotation: EFieldRegularBoolean = EFieldRegularBoolean(
//            property = data.campaign.resources::ignoreRotation,
//            onChange = { makeChange() })

        val defaultVisualLand: EFieldRegularLong = EFieldRegularLong(
            property = data.defaults.visual::land,
            onChange = { makeChange() })

        val defaultVisualPort: EFieldRegularLong = EFieldRegularLong(
            property = data.defaults.visual::port,
            onChange = { makeChange() })

        var change by mutableStateOf(false)
            private set

        fun updateOrigin() {
            origin = makeCopy()
            makeChange()
        }

        private fun makeCopy() = data.copy(
            campaign = data.campaign.copy(
                resources = data.campaign.resources.copy()
            ),
            defaults = data.defaults.copy(
                visual = data.defaults.visual.copy()
            )
        )

        private fun makeChange() {
            change = if (name.err) false
            else if (origin.name != data.name) true
            else if (origin.campaign.type != data.campaign.type) true
            else if (origin.defaults.visual.land != data.defaults.visual.land) true
            else if (origin.defaults.visual.port != data.defaults.visual.port) true
            else if (origin.campaign.itemCount != data.campaign.itemCount) true
            else if (origin.campaign.itemDuration != data.campaign.itemDuration) true
            else if (origin.campaign.enableCommercial != data.campaign.enableCommercial) true
            else if (origin.campaign.enableCommercialAsNonCommercial != data.campaign.enableCommercialAsNonCommercial) true
            else if (origin.campaign.resources.ignoreAspectRatio != data.campaign.resources.ignoreAspectRatio) true
            else if (origin.campaign.resources.ignoreDuration != data.campaign.resources.ignoreDuration) true
//            else if (origin.campaign.resources.ignoreRotation != data.campaign.resources.ignoreRotation) true
            else false
        }
    }

    class SellMode(
        val type: CampaignType,
        override val label: StringResource = when (type) {
            CampaignType.IMPRESSION -> Res.string.company_detail__info__sell_mode_impression
            CampaignType.NONE -> Res.string.company_detail__info__sell_mode_none
            CampaignType.SLOT -> Res.string.company_detail__info__sell_mode_slot
        },
        override val image: ImageVector? = null
    ) : DropDownResItem {
        companion object {
            val all by lazy {
                listOf(
                    SellMode(CampaignType.NONE),
                    SellMode(CampaignType.SLOT),
                    SellMode(CampaignType.IMPRESSION)
                )
            }
        }
    }
}


